<template>
  <div class="brief-content-view">
    <SelectFolderModalToolbar
      :value="searchKey"
      band
      placeholder="Search…"
      @input="onSearchInput"
      @clear="clearSearch"
    >
      <template #breadcrumb>
        <slot
          name="breadcrumb"
          :title="briefTitle"
        />
      </template>
    </SelectFolderModalToolbar>

    <div
      v-if="isLoading"
      class="brief-content-view__state"
    >
      <b-spinner />
    </div>

    <div
      v-else-if="visibleItems.length === 0"
      class="brief-content-view__state"
    >
      {{ emptyStateText }}
    </div>

    <template v-else>
      <ItemGrid
        class="brief-content-view__grid"
        :items="pagedItems"
        :columns="PRODUCT_PICKER_COLUMNS"
        :columns-md="PRODUCT_PICKER_COLUMNS"
        :is-selected="isSelected"
        :item-props="{ showCheckbox: true, showDownloadIcon: true }"
        @item-click="toggleSelect"
        @download="downloadItem"
      />
      <ItemPagination
        v-if="visibleItems.length > PRODUCT_PICKER_PAGE_SIZE"
        label-only
        :value="page"
        :per-page="PRODUCT_PICKER_PAGE_SIZE"
        :total-rows="visibleItems.length"
        @change="page = $event"
      />
    </template>
  </div>
</template>

<script>
import ItemGrid from '@frontend/components/modules/file-browser/browser/ItemGrid.vue';
import ItemPagination from '@frontend/components/modules/file-browser/browser/ItemPagination.vue';
import SelectFolderModalToolbar from '@frontend/components/modules/file-browser/modals/SelectFolderModalToolbar.vue';
import { PRODUCT_PICKER_COLUMNS, PRODUCT_PICKER_PAGE_SIZE } from '@frontend/constants/select-image-grid';
import { downloadFileFromUrl } from '@frontend/utils';
import { getBrief } from '@frontend/components/briefs/briefsApi';
import { collectBriefPickerImages } from '@frontend/components/briefs/briefReferenceUtils';
import { FILE_TYPE } from '@frontend/constants/file-image-types';

export default {
  name: 'BriefContentView',

  components: {
    ItemGrid,
    ItemPagination,
    SelectFolderModalToolbar,
  },

  props: {
    briefId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      items: [],
      searchKey: '',
      page: 1,
      selectedItems: [],
      PRODUCT_PICKER_COLUMNS,
      PRODUCT_PICKER_PAGE_SIZE,
      isLoading: false,
      loadFailed: false,
      briefTitle: '',
    };
  },

  computed: {
    emptyStateText() {
      if (this.loadFailed) {
        return 'Could not load the brief images.';
      }

      return this.searchKey.trim() ? 'No images match your search.' : 'No images found in this brief.';
    },
    visibleItems() {
      const query = this.searchKey.trim().toLowerCase();

      if (!query) {
        return this.items;
      }

      return this.items.filter((item) => (item.name || '').toLowerCase().includes(query));
    },
    pagedItems() {
      const start = (this.page - 1) * PRODUCT_PICKER_PAGE_SIZE;

      return this.visibleItems.slice(start, start + PRODUCT_PICKER_PAGE_SIZE);
    },
  },

  watch: {
    briefId() {
      this.fetchReferences();
    },
  },

  created() {
    this.fetchReferences();
  },

  methods: {
    async fetchReferences() {
      const requestedBriefId = this.briefId;

      this.items = [];
      this.selectedItems = [];
      this.searchKey = '';
      this.page = 1;
      this.loadFailed = false;
      this.briefTitle = '';
      this.isLoading = true;
      this.$emit('onSelectedItems', []);

      try {
        const res = await getBrief(requestedBriefId, { internal: true });

        // A slower earlier request must not overwrite the brief the user is on now.
        if (requestedBriefId !== this.briefId) {
          return;
        }

        const brief = res && res.brief ? res.brief : null;

        this.briefTitle = (brief && brief.title) || '';

        // Inspiration rows plus images embedded in every section (Creative Direction
        // and the rest). The same file can sit in more than one section.
        this.items = collectBriefPickerImages(brief).map((image) => this.toBrowserItem(image));
      } catch (error) {
        if (requestedBriefId !== this.briefId) {
          return;
        }

        console.error('Error fetching brief references:', error);
        this.loadFailed = true;
        toastr.error('Failed to load brief images');
      } finally {
        if (requestedBriefId === this.briefId) {
          this.isLoading = false;
        }
      }
    },

    // drawFileSvg needs the normalized 'image' token, not the extension, or it renders nothing.
    toBrowserItem(image) {
      return {
        id: image.id,
        name: image.name,
        type: FILE_TYPE.IMAGE,
        path: image.url,
        url: image.url,
        asset_type: 'file_asset',
      };
    },

    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },

    onSearchInput(value) {
      this.searchKey = value;
      this.page = 1;
    },

    clearSearch() {
      this.searchKey = '';
      this.page = 1;
    },

    toggleSelect(item) {
      this.selectedItems = this.isSelected(item)
        ? this.selectedItems.filter((i) => i.id !== item.id)
        : [...this.selectedItems, item];

      this.$emit('onSelectedItems', this.selectedItems);
    },

    async downloadItem(item) {
      await downloadFileFromUrl(item.url, item.name);
    },
  },
};
</script>

<style scoped>
.brief-content-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.brief-content-view__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #73808c;
}

.brief-content-view__grid {
  overflow: hidden;
}
</style>
